import { ClickAwayListener } from '@byecode/ui'
import type {
    ActorEvent,
    Field,
    FieldADTValue,
    HighLightConditions,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewField
} from '@lighthouse/core'
import type { FetchPexelsParams, PexelsResponse, UseUploadFileSParameter } from '@lighthouse/shared'
import {
    getIdByCellDataKey,
    getIsAppointField,
    NO_EDIT_SYSTEM_FIELD,
    notEditableFieldTypes,
    selectionState,
    universalHotKeys,
    VIEW_CELL_DATA_KEY
} from '@lighthouse/shared'
import type { UploadyProps } from '@rpldy/uploady'
import { find } from 'rambda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import AdvancedTableBody from './AdvancedTableBody'
import { TablePopover } from './AdvancedTablePopover'
import { AdvancedTableTooltip } from './AdvancedTableTooltip'

interface AdvancedTableContentProps {
    checkable?: boolean
    viewFields: ViewField[]
    scrollRef: React.RefObject<HTMLDivElement>
    blockWidth: number
    selectedRecords?: string[]
    highLightRules?: HighLightConditions
    primaryDataSourceFieldIds?: Set<string>
    uploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    richTextUploadOptions: UploadyProps
    videoUploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    onRecordSelect?: (recordIds: string[]) => void
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onCellUpdate?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
    onFetchPexelsList: (params: FetchPexelsParams) => Promise<PexelsResponse>
}

export interface AdvancedTableContentState {
    currentRecordId: string
    currentWidth?: number
    currentField?: Field
    popperAnchorElement?: HTMLDivElement
}

export interface AdvancedTableTooltipState {
    toolTipAnchorElement?: HTMLDivElement
    message?: string
}

const SCxTableAdvancedContent = styled.div`
    height: 100%;
    min-width: 100%;
    width: fit-content;
    outline: none;
`

export const AdvancedTableContent: React.FC<AdvancedTableContentProps> = ({
    checkable,
    viewFields,
    scrollRef,
    blockWidth,
    selectedRecords,
    highLightRules,
    primaryDataSourceFieldIds,
    uploadOptions,
    richTextUploadOptions,
    videoUploadOptions,
    onRecordSelect,
    onCellChange,
    onCellUpdate,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderButtonTitle,
    onFetchPexelsList
}) => {
    const { dataSource } = useViewBlockContext()
    const { schema, appId: dsAppId, id: dsId } = dataSource
    const [state, setState] = useState<AdvancedTableContentState>({
        currentRecordId: '',
        currentWidth: 160,
        currentField: undefined,
        popperAnchorElement: undefined
    })

    const [tooltipState, setTooltipState] = useState<AdvancedTableTooltipState>({
        toolTipAnchorElement: undefined
    })

    const { currentRecordId, currentWidth, currentField, popperAnchorElement } = state

    useEffect(() => {
        if (tooltipState.toolTipAnchorElement) {
            const timer = setTimeout(() => {
                setTooltipState({
                    toolTipAnchorElement: undefined
                })
            }, 1500)
            return () => clearTimeout(timer)
        }
    }, [setTooltipState, tooltipState.toolTipAnchorElement])

    const handleOpenTooltip = useCallback(
        (data: AdvancedTableTooltipState) => {
            setTooltipState(data)
        },
        [setTooltipState]
    )

    const handleOpenEditor = useCallback(
        (data: AdvancedTableContentState) => {
            setState(data)
        },
        [setState]
    )

    const handleClose = useCallback(() => {
        setState({
            currentRecordId: '',
            currentWidth: 160,
            currentField: undefined,
            popperAnchorElement: undefined
        })
    }, [])

    const handleBlur = useCallback(() => {
        if (!scrollRef.current) {
            return
        }
        const cellActiveDoms = scrollRef.current.querySelectorAll('.cell-active')
        cellActiveDoms.forEach(cellActiveDom => {
            cellActiveDom.classList.remove('cell-active')
        })
    }, [scrollRef])

    useHotkeys<HTMLDivElement>(
        universalHotKeys,
        ev => {
            const { cellCheckedId } = selectionState.state
            if (!scrollRef.current) {
                return
            }
            const cDom = scrollRef.current.querySelector(`[${VIEW_CELL_DATA_KEY}='${cellCheckedId}']`) as HTMLDivElement
            if (!cellCheckedId || !cDom) {
                return
            }
            const disable = cDom.getAttribute('data-disable')
            if (disable) {
                return
            }
            const anchorDom = cDom.querySelector(`[data-type='anchor']`) as HTMLDivElement
            const [_, recordId, fieldId] = getIdByCellDataKey(cellCheckedId)
            const field = schema[fieldId]
            const tableProp = find(item => item.fieldId === fieldId, viewFields)

            if (
                !primaryDataSourceFieldIds?.has(fieldId) ||
                notEditableFieldTypes.has(field?.type) ||
                getIsAppointField(fieldId, NO_EDIT_SYSTEM_FIELD) ||
                !anchorDom
            ) {
                return
            }

            ev.preventDefault()
            setState({
                popperAnchorElement: anchorDom,
                currentRecordId: recordId,
                // currentData: record.content[fieldId].value,
                currentWidth: tableProp?.width || 160,
                currentField: field
            })
        },
        { enableOnContentEditable: true },
        [viewFields]
    )

    const tableTooltip = useMemo(() => {
        if (!tooltipState.toolTipAnchorElement || !tooltipState.message) {
            return null
        }
        return <AdvancedTableTooltip target={tooltipState.toolTipAnchorElement} label={tooltipState.message} />
    }, [tooltipState.message, tooltipState.toolTipAnchorElement])

    const tablePopover = useMemo(() => {
        if (!currentField) {
            return null
        }

        const currentFieldProp = find(({ fieldId }) => fieldId === currentField.id, viewFields)

        if (!currentFieldProp) {
            return null
        }

        const { required } = currentFieldProp

        return (
            <TablePopover
                scrollRef={scrollRef}
                popperAnchorElement={popperAnchorElement}
                recordId={currentRecordId}
                width={currentWidth}
                required={required}
                field={currentField}
                uploadOptions={uploadOptions}
                richTextUploadOptions={richTextUploadOptions}
                videoUploadOptions={videoUploadOptions}
                onCellChange={onCellChange}
                onCellUpdate={onCellUpdate}
                onClose={handleClose}
                onFetchPexelsList={onFetchPexelsList}
            />
        )
    }, [
        currentField,
        viewFields,
        scrollRef,
        popperAnchorElement,
        currentRecordId,
        currentWidth,
        uploadOptions,
        richTextUploadOptions,
        videoUploadOptions,
        onCellChange,
        onCellUpdate,
        handleClose,
        onFetchPexelsList
    ])

    return (
        <ClickAwayListener mouseEvent="onMouseDown" onClickAway={() => handleBlur()}>
            <SCxTableAdvancedContent>
                <AdvancedTableBody
                    checkable={checkable}
                    selectedRecords={selectedRecords}
                    viewFields={viewFields}
                    scrollRef={scrollRef}
                    highLightRules={highLightRules}
                    blockWidth={blockWidth}
                    primaryDataSourceFieldIds={primaryDataSourceFieldIds}
                    onRecordSelect={onRecordSelect}
                    onOpenEditor={handleOpenEditor}
                    onOpenTooltip={handleOpenTooltip}
                    onCellChange={onCellChange}
                    onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                    onRecordClick={onRecordClick}
                    onRenderButtonTitle={onRenderButtonTitle}
                />
                {tablePopover}
                {tableTooltip}
            </SCxTableAdvancedContent>
        </ClickAwayListener>
    )
}
